'use client';
import React, { useState } from 'react';
import { SessionContext } from '@benzinga/session-context';
import { useActiveWorkspaceSubscriber } from '@benzinga/pro-tools';
import { Security } from '@benzinga/quotes-v3-manager';
import { QuotesV3HoldingsManager, HoldingsQuote } from '@benzinga/quotes-v3-holdings-manager';
import Hooks from '@benzinga/hooks';

export const useQuoteHoldingsSubscription = (
  symbols: Security[],
  fields: Set<keyof HoldingsQuote>,
): HoldingsQuote[] | undefined => {
  const [quotes, setQuotes] = useState<HoldingsQuote[] | undefined>(undefined);
  useQuoteHoldingsSubscriptionCallback(symbols, fields, setQuotes);
  return quotes;
};

export const useQuoteHoldingsSubscriptionCallback = (
  symbols: Security[],
  fields: Set<keyof HoldingsQuote>,
  setter: (quote: HoldingsQuote[]) => void,
) => {
  const session = React.useContext(SessionContext);
  const quotesManager = session.getManager(QuotesV3HoldingsManager);

  // The feed must not be rebuilt just because the setter's identity changed: a new feed
  // only knows about `symbols`, so every symbol added later through addSymbolsToFeed
  // would silently lose its subscription.
  const setterRef = React.useRef(setter);
  setterRef.current = setter;

  const feed = React.useMemo(() => {
    const feed = quotesManager.createQuotesFeed(symbols, true);
    setterRef.current(feed.getCachedValue());
    return feed;
  }, [quotesManager, symbols]);

  const stableSetter = React.useCallback((quotes: HoldingsQuote[]) => setterRef.current(quotes), []);
  const [throttledCallback] = Hooks.useThrottle(stableSetter, 1000);

  useActiveWorkspaceSubscriber(
    feed,
    event => {
      switch (event.type) {
        case 'quote-holdings:update':
          throttledCallback(feed.getCachedValue());
          break;
      }
    },
    fields,
  );

  const addSymbolsToFeed = React.useCallback((symbols: Security[]) => feed.addSymbols(symbols), [feed]);

  const getSymbolsFromFeed = React.useCallback(() => feed.getSymbols(), [feed]);

  const removeSymbolsFromFeed = React.useCallback((symbols: Security[]) => feed.removeSymbols(symbols), [feed]);

  return React.useMemo(
    () => ({
      addSymbolsToFeed,
      getSymbolsFromFeed,
      removeSymbolsFromFeed,
    }),
    [addSymbolsToFeed, getSymbolsFromFeed, removeSymbolsFromFeed],
  );
};
